import { useLeaveRun } from '@sorrem-client/ui/features/PlayPage/use-leave-run';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { clearStoredCrucibleJoinSession } from '@sorrem-client/ui/features/PlayPage/stored-join-session';
import { useConfirm, useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { getStoredSessionToken } from '@sorrem-client/ui/shared/auth-session';
import { isApiFailure, resolveApiErrorMessage, useLeaveCrucibleMutation } from '@sorrem-client/ui/store';
import { useCallback, useRef, useState } from 'react';

export interface QuitCrucibleState {
    /** Asks the player to confirm, then leaves the crucible for good and goes back to Character Home. */
    quit: () => Promise<void>;
    isQuitting: boolean;
    /** Why the last quit failed, or null. */
    quitError: string | null;
    dismissQuitError: () => void;
}

/** Said when the server gave no reason: the request never got an answer, or failed without one. */
const QUIT_FAILED_MESSAGE = 'Unable to quit crucible. Please try again.';

/**
 * Quitting the run from its menu binding: a confirm, then the membership is
 * removed, the stored join forgotten and the game left. `hasRunEnded`
 * blocks it once the run is over.
 */
export function useQuitCrucible(hasRunEnded: boolean): QuitCrucibleState {
    const leaveRun = useLeaveRun();
    const { confirm } = useConfirm();
    const { requestLeaveRun, crucibleId } = useGameplay();
    const { selectedCharacterId } = useSelectedCharacterId();
    const [leaveCrucible] = useLeaveCrucibleMutation();
    const [isQuitting, setIsQuitting] = useState(false);
    const [quitError, setQuitError] = useState<string | null>(null);
    // Guards against a second prompt while the first is still open.
    const isQuitPromptActiveRef = useRef(false);

    const quit = useCallback(async (): Promise<void> => {
        if (isQuitting || isQuitPromptActiveRef.current || hasRunEnded) {
            return;
        }

        isQuitPromptActiveRef.current = true;
        const didConfirm = await confirm({
            title: 'Quit this crucible?',
            description: 'You will leave this run as a failed participant. Your party will continue without you.',
            confirmLabel: 'Quit',
            cancelLabel: 'Stay',
        });

        if (!didConfirm || isQuitting) {
            isQuitPromptActiveRef.current = false;
            return;
        }

        setIsQuitting(true);
        setQuitError(null);

        try {
            const token = getStoredSessionToken();

            if (!token || !crucibleId || !selectedCharacterId) {
                setQuitError('Unable to resolve your current crucible session.');
                return;
            }

            try {
                await leaveCrucible({ crucibleId, characterId: selectedCharacterId }).unwrap();
            } catch (failure) {
                // Already gone from the crucible: quitting carries on.
                if (!isApiFailure(failure) || failure.status !== 404) {
                    setQuitError(resolveApiErrorMessage(failure, QUIT_FAILED_MESSAGE));
                    return;
                }
            }

            clearStoredCrucibleJoinSession();
            // Quitting is final: the Crucible removes them rather than holding an echo for a rejoin.
            requestLeaveRun();
            await leaveRun('crucibles');
        } catch {
            setQuitError(QUIT_FAILED_MESSAGE);
        } finally {
            setIsQuitting(false);
            isQuitPromptActiveRef.current = false;
        }
    }, [confirm, crucibleId, hasRunEnded, isQuitting, leaveCrucible, leaveRun, requestLeaveRun, selectedCharacterId]);

    const dismissQuitError = useCallback((): void => setQuitError(null), []);

    return { quit, isQuitting, quitError, dismissQuitError };
}
